<template>
  <div
    class="rechercheViewWrapper"
    ref="rechercheViewWrapper"
  >
    <div class="searchWrapper searchWrapper--desktop">
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        class="searchInput"
        size="large"
        state="default"
        placeholder="Référence facture / avoir, Numéro de dossier, SIRET..."
        :maxlength="80"
        deletable
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
      />

      <div class="searchWrapper--actions">
        <BasicTooltip label="Exporter les factures">
          <BasicButton
            @click="exporter"
            size="large"
            icon-name="export"
            variant="outlined"
          />
        </BasicTooltip>
        <BasicButton
          v-if="readonlyRib === false"
          type="secondary"
          label="Déposer un RIB"
          size="large"
          @click="redirectSelfeRib"
        />
        <BasicButton
          v-if="readonlyFacture === false"
          label="Déposer une facture"
          size="large"
          @click="redirectSelfeFacture"
        />
      </div>
    </div>
    <div class="searchWrapper searchWrapper--mobile">
      <div class="flex flex-gap-4">
        <BasicButton
          @click="exporter"
          label="Exporter"
          size="large"
          icon-name="export"
          variant="outlined"
        />
        <BasicButton
          v-if="readonlyFacture === false"
          @click="redirectSelfeFacture"
          label="Déposer une facture"
          icon-name="bill"
          size="large"
          width="full"
        />
      </div>
      <BasicButton
        v-if="readonlyRib === false"
        @click="redirectSelfeRib"
        label="Déposer un RIB"
        icon-name="document-text"
        type="secondary"
        size="large"
        width="full"
      />

      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        size="large"
        state="default"
        placeholder="Référence facture / avoir, Numéro de dossier..."
        :maxlength="80"
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
        width="full"
      />
    </div>
    <Transition>
      <div
        class="filtersWrapper"
        v-if="showFilters"
      >
        <WrapperDropdownMultiple
          v-model="statuts"
          label="Statut"
          :items="statutsItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />
        <WrapperDropdown
          v-model="periodeDateReception"
          label="Période de réception"
          :items="periodeDateReceptionItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />
        <WrapperDropdown
          v-model="periodeDateEmission"
          label="Période d'émission"
          :items="periodeDateEmissionItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />
        <div class="col-4 col-sm-36 actions--item">
          <BasicTooltip label="Ajouter les filtres aux favoris">
            <BasicButton
              :disabled="!isAdvancedFiltered"
              type="secondary"
              iconName="star"
              @click="addFavori"
              style="display: inline"
            />
          </BasicTooltip>
          <BasicTooltip label="Supprimer les filtres">
            <BasicButton
              @click="removeFilters"
              :disabled="!isAdvancedFiltered"
              type="reverse"
              iconName="filter-remove"
              style="display: inline"
            />
          </BasicTooltip>
        </div>
      </div>
    </Transition>
    <div class="informationWrapper">
      <BasicPagination
        :nbPages="pageCount"
        :nbPagesMax="10"
        :currentPage="pageNumber"
        :nbResults="store.pageData?.totalRecords"
        @change="pageChange"
      />
      <BasicDropdown
        v-if="pageSizeVisible"
        v-model="pageSize"
        :items="pageSizeItems"
        placeholder="Nombre de résultats"
        width="auto"
        force-value
      />
    </div>
    <div
      class="listComponentMainWrapper"
      ref="listComponentMainWrapper"
    >
      <div class="mobileWrapper">
        <FactureCardMobile
          v-for="(item, index) in pageData?.data"
          :key="index"
          :item
        />
      </div>
      <div class="desktopWrapper">
        <FactureHeader v-model="order" />
        <FactureCardDesktop
          v-for="(item, index) in pageData?.data"
          class="listCardDesktopWrapper"
          :key="index"
          :item
        />
      </div>
      <cards-layout
        v-if="!pageData?.data.length"
        cardsType="Factures"
        headerText="Factures"
        :hasHeader="false"
        :cards="[]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount } from 'vue'
  import { StatutFactureLibelle } from '@/features/api/extranet/dto/Facture/StatutFactureDTO'
  import { PeriodeLibelle } from '@/features/domaine/types/opco/shared/Periode'
  import { objectToDropdownItems } from '../../shared/tools'
  import { pageSizeItems } from '../shared/dropdown-items.ts'
  import { FactureCardDesktop, FactureCardMobile, FactureHeader } from './components'
  import { useRechercheFactureActions } from './useRechercheFactureActions'
  import { useRechercheFactureStore } from './useRechercheFactureStore'
  import CardsLayout from '@/features/interface/home/tableauDeBord/cardsLayout/CardsLayout.vue'
  import { type NavigateToQueryFactures, type WithNavigateToLocation } from '@/router/navigateTo'
  import { onBeforeRouteUpdate } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { usePagination, useShowFilter } from '@/features/interface/recherches/shared'

  defineProps<WithNavigateToLocation<NavigateToQueryFactures>>()

  const store = useRechercheFactureStore()
  onBeforeUnmount(store.$reset)
  const { setupFieldWithKey, exportData } = store
  const { pageData, order, search } = storeToRefs(store)
  const { showFilters, removeFilters, isAdvancedFiltered } = useShowFilter(store)
  const { pageCount, pageSize, pageNumber, pageChange, pageSizeVisible } = usePagination(store)

  const statutsItems = objectToDropdownItems(StatutFactureLibelle)
  const periodeDateReceptionItems = objectToDropdownItems(PeriodeLibelle)
  const periodeDateEmissionItems = objectToDropdownItems(PeriodeLibelle)

  const { addFavori, redirectSelfeFacture, redirectSelfeRib, readonlyFacture, readonlyRib } =
    useRechercheFactureActions()

  const statuts = setupFieldWithKey<string[]>('query.statuts', true)
  const periodeDateReception = setupFieldWithKey<string>('query.periodeDateReception')
  const periodeDateEmission = setupFieldWithKey<string>('query.periodeDateEmission')

  /* Exporter les factures */
  const exporter = () =>
    exportData({
      title: 'Export des factures',
      message: 'Les factures ont été exportées avec succès, vous allez les recevoir par mail',
    })

  onBeforeRouteUpdate((to, ...args) => {
    return to.meta.navigateToHelper?.beforeEnter?.call(null, to, ...args)
  })
</script>

<style scoped lang="less">
  @import '../index.less';
</style>
